import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { INSTALL_URL } from "./Installation";
import { scrollToId } from "../../lib/scroll";
import { LogoMark } from "./Logo";
import { useLang } from "../../lib/i18n";

const EASE = [0.16, 1, 0.3, 1];

const MaskLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.12em]">
    <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.2, ease: EASE, delay }}>
      {children}
    </motion.span>
  </span>
);

const FadeUp = ({ children, delay, className }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay }}>
    {children}
  </motion.div>
);

const RotatingBadge = () => {
  const { t } = useLang();
  return (
  <div className="sw-spin absolute -left-10 -top-10 z-20 hidden h-28 w-28 items-center justify-center rounded-full bg-[#E6D9BC] text-[#1E2A32] sm:flex">
    <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full">
      <defs><path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
      <text className="font-mono" fontSize={t.badge.length > 42 ? 6.8 : 8} letterSpacing={t.badge.length > 42 ? 0.8 : 1.2} fill="#1E2A32">
        <textPath href="#circ">{t.badge}</textPath>
      </text>
    </svg>
    <LogoMark className="sw-spin-rev h-7 w-auto" />
  </div>
  );
};

const TiltFrame = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={reset} className="relative [perspective:1400px]" data-testid="hero-visual">
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative [transform-style:preserve-3d]">
        <RotatingBadge />
        <motion.div
          className="relative aspect-[4/5] overflow-hidden rounded-t-[999px] rounded-b-[28px] border border-[#E6D9BC]/20"
          initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: 1.6, ease: EASE, delay: 0.3 }}
        >
          <motion.img style={{ y: imgY }} src="/img/studio-v2.jpeg" alt="Studio furnished with the Studio pack" className="absolute inset-0 h-[118%] w-full -translate-y-[9%] scale-105 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1E2A32]/70 via-transparent to-transparent" />
        </motion.div>
        <motion.div
          style={{ transform: "translateZ(60px)" }}
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4, duration: 0.9, ease: EASE }}
          className="absolute -bottom-6 right-4 rounded-2xl border border-[#E6D9BC]/20 bg-[#162026]/85 px-5 py-4 backdrop-blur-xl sm:-right-6"
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#E6D9BC]">{t.heroCard}</p>
          <p className="mt-1 font-display text-2xl text-[#F4EFE6]">20 – 30 m²</p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const { t, lang } = useLang();
  return (
  <section id="home" data-testid="home-section" className="relative overflow-hidden pt-28 md:pt-36">
    <div className="pointer-events-none absolute -right-40 top-20 h-[620px] w-[620px] rounded-full bg-[#E6D9BC]/[0.06] blur-3xl" />
    <div className="mx-auto grid max-w-[1440px] gap-16 px-5 pb-24 md:px-10 lg:grid-cols-12 lg:gap-10 lg:pb-32">
      <div className="lg:col-span-7 lg:pt-10">
        <FadeUp delay={0.1} className="mb-8 flex items-center gap-4 font-mono text-xs uppercase tracking-[0.25em] text-[#E6D9BC]">
          <span className="h-px w-10 bg-[#E6D9BC]/60" /> {t.heroEyebrow}
        </FadeUp>
        <h1 key={lang} data-testid="hero-title" className="font-display text-[2.6rem] leading-[0.98] tracking-[-0.02em] text-[#F4EFE6] sm:text-7xl lg:text-[6rem]">
          <MaskLine delay={0.2}>{t.heroLines[0]}</MaskLine>
          <MaskLine delay={0.32}>{t.heroLines[1]}<em className="text-[#E6D9BC]">{t.heroLines[2]}</em></MaskLine>
          <MaskLine delay={0.44}>{t.heroLines[3]}</MaskLine>
        </h1>
        <FadeUp delay={0.9} className="mt-10 max-w-xl text-base leading-relaxed text-[#BAC6CE] md:text-lg">
          {t.heroBody}
        </FadeUp>
        <FadeUp delay={1.05} className="mt-10 flex flex-wrap items-center gap-4">
          <button data-testid="hero-order-now-button" onClick={() => scrollToId("order-pack")} className="sw-btn group">
            {t.heroCta} <ArrowDownRight size={18} className="transition-transform duration-300 group-hover:rotate-[-45deg]" />
          </button>
          <a data-testid="hero-installer-link" href={INSTALL_URL} target="_blank" rel="noopener noreferrer" className="sw-btn-ghost group text-left">
            {t.heroContact} <ArrowUpRight size={18} className="shrink-0 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </a>
        </FadeUp>
        <FadeUp delay={1.2} className="mt-16 grid max-w-xl grid-cols-3 gap-6 border-t border-[#E6D9BC]/15 pt-6">
          {[["02", t.stats[0]], ["20–40", t.stats[1]], ["1", t.stats[2]]].map(([n, l]) => (
            <div key={l}>
              <p className="font-display text-3xl text-[#F4EFE6] md:text-4xl">{n}</p>
              <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-[#A3B2BC]">{l}</p>
            </div>
          ))}
        </FadeUp>
      </div>
      <div className="mx-auto w-full max-w-md lg:col-span-5 lg:max-w-none lg:pl-8">
        <TiltFrame />
      </div>
    </div>
  </section>
  );
};
